/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Notification Manager - Global notification state management
 * Updated: 2026-01-09
 */

export type NotificationType = 'loading' | 'error' | 'success' | 'info';

export interface Notification {
  id: string;
  type: NotificationType;
  message: string;
  action?: string;
  onAction?: () => void | Promise<void>;
  duration?: number; // ms, null = persistent
  details?: string;
}

class NotificationManagerClass {
  private listeners: Set<(notifications: Notification[]) => void> = new Set();
  private notifications: Notification[] = [];
  private nextId = 0;

  subscribe(listener: (notifications: Notification[]) => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify() {
    this.listeners.forEach(listener => listener([...this.notifications]));
  }

  private generateId(): string {
    return `notification_${++this.nextId}_${Date.now()}`;
  }

  show(notification: Notification): string {
    const id = notification.id || this.generateId();
    const fullNotification: Notification = {
      ...notification,
      id
    };

    this.notifications.push(fullNotification);
    this.notify();

    // Auto-dismiss if duration is set
    if (fullNotification.duration && fullNotification.duration > 0) {
      setTimeout(() => this.dismiss(id), fullNotification.duration);
    }

    return id;
  }

  dismiss(id: string) {
    this.notifications = this.notifications.filter(n => n.id !== id);
    this.notify();
  }

  dismissAll() {
    this.notifications = [];
    this.notify();
  }

  showLoading(message: string, onCancel?: () => void): string {
    return this.show({
      id: `loading_${this.generateId()}`,
      type: 'loading',
      message,
      action: onCancel ? 'Cancel' : undefined,
      onAction: onCancel,
      duration: null // Persist until dismissed
    });
  }

  showError(message: string, onRetry?: () => void | Promise<void>, details?: string): string {
    return this.show({
      id: `error_${this.generateId()}`,
      type: 'error',
      message,
      action: onRetry ? 'Retry' : undefined,
      onAction: onRetry,
      duration: null, // Persist until dismissed
      details
    });
  }

  showSuccess(message: string, duration: number = 3000): string {
    return this.show({
      id: `success_${this.generateId()}`,
      type: 'success',
      message,
      duration
    });
  }

  showInfo(message: string, duration: number = 3000): string {
    return this.show({
      id: `info_${this.generateId()}`,
      type: 'info',
      message,
      duration
    });
  }

  getAll(): Notification[] {
    return [...this.notifications];
  }
}

export const notificationManager = new NotificationManagerClass();
